import {
  type InitOptions,
  type Resource,
  createInstance as createInstanceImpl,
} from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { CogI18nInstance, I18nInstance, TypedT } from '../types/i18n';
import {
  lowercasePostProcessor,
  uppercasePostProcessor,
} from './postProcessor';
import { type selectLanguage as SelectLanguageFn } from './i18n';
import { overrideFontFamily as overrideFontFamilyImpl } from './overrideFontFamily';
import { addNamespaceResources as addNamespaceResourcesImpl } from './addNamespaceResources';

export type CreateCogI18nInstanceProps = {
  language: string | undefined;
  translations: Resource;
  defaultNamespace?: string;
  selectLanguage: typeof SelectLanguageFn;
};

export type CreateCogI18nInstanceDependencies = {
  createInstance: (
    ...args: Parameters<typeof createInstanceImpl>
  ) => I18nInstance;
  overrideFontFamily: typeof overrideFontFamilyImpl;
  addNamespaceResources: typeof addNamespaceResourcesImpl;
};

const defaultDeps: CreateCogI18nInstanceDependencies = {
  createInstance: createInstanceImpl,
  overrideFontFamily: overrideFontFamilyImpl,
  addNamespaceResources: addNamespaceResourcesImpl,
};

/**
 * Initializes (or reuses an already-initialized) i18next instance and wraps
 * it in the typed Cognite i18n contract.
 *
 * Exposes:
 * * `t` - type safe translate function
 * * `changeLanguage` - changes and persists the selected language
 * * `language` - reads the instance's current value
 * * `addResources` - merges further translations onto the instance later
 */
export async function createCogI18nInstance<
  const Translations extends Record<string, string>,
>(
  {
    language,
    translations,
    defaultNamespace,
    selectLanguage,
  }: CreateCogI18nInstanceProps,
  instance?: I18nInstance,
  dependencies: CreateCogI18nInstanceDependencies = defaultDeps
): Promise<CogI18nInstance<Translations>> {
  const { addNamespaceResources, createInstance, overrideFontFamily } =
    dependencies;
  const resolvedInstance = instance ?? createInstance();

  if (resolvedInstance.isInitialized) {
    addNamespaceResources(resolvedInstance, translations, defaultNamespace);
  } else {
    const commonI18nOptions: Pick<
      InitOptions,
      'lng' | 'fallbackLng' | 'keySeparator' | 'interpolation' | 'defaultNS'
    > = {
      lng: language,
      fallbackLng: 'en',
      keySeparator: false,
      interpolation: {
        escapeValue: false,
      },
      defaultNS: defaultNamespace,
    };

    await resolvedInstance
      .use(initReactI18next)
      .use(lowercasePostProcessor)
      .use(uppercasePostProcessor)
      .init({
        ...commonI18nOptions,
        resources: translations,
      });
  }

  async function changeLanguage(newLanguage: string): Promise<void> {
    selectLanguage(newLanguage);
    await resolvedInstance.changeLanguage(newLanguage);
    overrideFontFamily(newLanguage);
  }

  // Type safety is enforced by TypedT — cast to bypass i18next's internal overloads.
  const t: TypedT<Translations> = (key, ...args) =>
    (resolvedInstance.t as (key: string, options?: unknown) => string)(
      key,
      args[0]
    );

  return {
    t,
    changeLanguage,
    addResources: (moreTranslations: Resource, addlDefaultNamespace?: string) =>
      addNamespaceResources(
        resolvedInstance,
        moreTranslations,
        addlDefaultNamespace
      ),
    get language() {
      return resolvedInstance.language;
    },
  };
}
